import { X, AlertCircle } from 'lucide-react';
import { useEffect } from 'react';

interface ReportFiltersModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedMetrics: string[];
    onMetricsChange: (metrics: string[]) => void;
    selectedDimensions: string[];
    onDimensionsChange: (dimensions: string[]) => void;
    compareWithPrevious: boolean;
    onCompareChange: (compare: boolean) => void;
    includeInsights: boolean;
    onInsightsChange: (insights: boolean) => void;
    includeGrowthAnalysis: boolean;
    onGrowthAnalysisChange: (growth: boolean) => void;
    topN: number | null;
    onTopNChange: (topN: number | null) => void;
    minImpressions: number | null;
    onMinImpressionsChange: (min: number | null) => void;
    alertThreshold: number;
    onAlertThresholdChange: (threshold: number) => void;
    selectedCountry: string;
    onCountryChange: (country: string) => void;
    selectedAdUnit: string;
    onAdUnitChange: (adUnit: string) => void;
}

const AVAILABLE_METRICS = [
    { value: 'impressions', label: 'Impressões' },
    { value: 'clicks', label: 'Cliques' },
    { value: 'revenue', label: 'Receita' },
    { value: 'ecpm', label: 'eCPM' },
    { value: 'ctr', label: 'CTR' },
    { value: 'active_view', label: 'Viewability' },
    { value: 'unfilled_impressions', label: 'Não Preenchidas' },
    { value: 'requests_served', label: 'Requisições' },
    { value: 'pmr', label: 'PMR' },
];

const AVAILABLE_DIMENSIONS = [
    { value: 'domain', label: 'Domínio' },
    { value: 'country', label: 'País' },
    { value: 'date', label: 'Data' },
    { value: 'ad_unit_name', label: 'Ad Unit' },
];

const AD_UNIT_TYPES = [
    { value: '', label: 'Todos' },
    { value: 'Rewarded', label: 'Rewarded' },
    { value: 'Topo', label: 'Topo' },
    { value: 'Content1', label: 'Content1' },
    { value: 'Content2', label: 'Content2' },
    { value: 'Interstitial', label: 'Interstitial' },
    { value: 'anchor', label: 'Anchor' },
    { value: 'fixed', label: 'Fixed' },
];

export function ReportFiltersModal({
                                               isOpen,
                                               onClose,
                                               selectedMetrics,
                                               onMetricsChange,
                                               selectedDimensions,
                                               onDimensionsChange,
                                               compareWithPrevious,
                                               onCompareChange,
                                               includeInsights,
                                               onInsightsChange,
                                               includeGrowthAnalysis,
                                               onGrowthAnalysisChange,
                                               topN,
                                               onTopNChange,
                                               minImpressions,
                                               onMinImpressionsChange,
                                               alertThreshold,
                                               onAlertThresholdChange,
                                               selectedCountry,
                                               onCountryChange,
                                               selectedAdUnit,
                                               onAdUnitChange,
                                           }: ReportFiltersModalProps) {
    // Bloquear scroll quando modal aberto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Desabilitar análise de crescimento se "date" estiver selecionado
    const isDateSelected = selectedDimensions.includes('date');
    const canUseGrowthAnalysis = !isDateSelected;

    // Se "date" foi selecionado e análise de crescimento estava ativa, desativa
    useEffect(() => {
        if (isDateSelected && includeGrowthAnalysis) {
            onGrowthAnalysisChange(false);
        }
    }, [isDateSelected, includeGrowthAnalysis, onGrowthAnalysisChange]);

    if (!isOpen) return null;

    const toggleMetric = (metric: string) => {
        if (selectedMetrics.includes(metric)) {
            onMetricsChange(selectedMetrics.filter((m) => m !== metric));
        } else {
            onMetricsChange([...selectedMetrics, metric]);
        }
    };

    const toggleDimension = (dimension: string) => {
        if (selectedDimensions.includes(dimension)) {
            onDimensionsChange(selectedDimensions.filter((d) => d !== dimension));
        } else {
            onDimensionsChange([...selectedDimensions, dimension]);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Filtros Avançados</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Customize seu relatório com métricas e dimensões específicas
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                        {/* Filtros de Texto */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* País */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    🌍 Filtrar por País
                                </label>
                                <input
                                    type="text"
                                    value={selectedCountry}
                                    onChange={(e) => onCountryChange(e.target.value)}
                                    placeholder="Ex: Brazil, Argentina..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Ad Unit - SELECT */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    📦 Filtrar por Ad Unit
                                </label>
                                <select
                                    value={selectedAdUnit}
                                    onChange={(e) => onAdUnitChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                >
                                    {AD_UNIT_TYPES.map((adUnit) => (
                                        <option key={adUnit.value} value={adUnit.value}>
                                            {adUnit.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Métricas */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                📊 Métricas
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                                {AVAILABLE_METRICS.map((metric) => (
                                    <button
                                        key={metric.value}
                                        onClick={() => toggleMetric(metric.value)}
                                        className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                                            selectedMetrics.includes(metric.value)
                                                ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                                                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                                        }`}
                                    >
                                        {metric.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Dimensões */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                📐 Dimensões (Agrupamento)
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {AVAILABLE_DIMENSIONS.map((dimension) => (
                                    <button
                                        key={dimension.value}
                                        onClick={() => toggleDimension(dimension.value)}
                                        className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                                            selectedDimensions.includes(dimension.value)
                                                ? 'bg-green-50 border-green-500 text-green-700 font-medium'
                                                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                                        }`}
                                    >
                                        {dimension.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Alerta sobre Data + Crescimento */}
                        {isDateSelected && (
                            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-amber-800">
                                    <p className="font-medium">Análise de Crescimento Desabilitada</p>
                                    <p className="text-amber-700 mt-1">
                                        A análise de crescimento não funciona quando a dimensão "Data" está selecionada.
                                        Remova "Data" das dimensões para habilitar esta análise.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Opções de Análise */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                🔍 Análises
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <label className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="checkbox"
                                        checked={compareWithPrevious}
                                        onChange={(e) => onCompareChange(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Comparar período anterior</span>
                                </label>

                                <label className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="checkbox"
                                        checked={includeInsights}
                                        onChange={(e) => onInsightsChange(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Insights automáticos</span>
                                </label>

                                <label
                                    className={`flex items-center gap-2 p-3 border rounded-lg transition-all ${
                                        canUseGrowthAnalysis
                                            ? 'border-gray-300 cursor-pointer hover:bg-gray-50'
                                            : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                    }`}
                                    title={!canUseGrowthAnalysis ? 'Remova a dimensão "Data" para usar esta análise' : ''}
                                >
                                    <input
                                        type="checkbox"
                                        checked={includeGrowthAnalysis && canUseGrowthAnalysis}
                                        onChange={(e) => canUseGrowthAnalysis && onGrowthAnalysisChange(e.target.checked)}
                                        disabled={!canUseGrowthAnalysis}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                                    />
                                    <span className={`text-sm ${canUseGrowthAnalysis ? 'text-gray-700' : 'text-gray-400'}`}>
                    Análise de crescimento
                  </span>
                                </label>
                            </div>
                        </div>

                        {/* Filtros Numéricos */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    🏆 Top N Resultados
                                </label>
                                <input
                                    type="number"
                                    value={topN || ''}
                                    onChange={(e) =>
                                        onTopNChange(e.target.value ? parseInt(e.target.value) : null)
                                    }
                                    placeholder="Todos"
                                    min="1"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    👁️ Impressões Mínimas
                                </label>
                                <input
                                    type="number"
                                    value={minImpressions || ''}
                                    onChange={(e) =>
                                        onMinImpressionsChange(e.target.value ? parseInt(e.target.value) : null)
                                    }
                                    placeholder="Sem filtro"
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    🚨 Alerta de Variação (%)
                                </label>
                                <input
                                    type="number"
                                    value={alertThreshold}
                                    onChange={(e) => onAlertThresholdChange(parseInt(e.target.value) || 15)}
                                    min="0"
                                    max="100"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Aplicar Filtros
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}